import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { createContext, ReactNode, RefObject, useContext, useEffect } from 'react';
import {
    ChapterListItemResponse,
    LessonResponse,
    UserLessonResponse,
} from '../../../../server/response/listeningResponseScheme';
import { LessonStatus } from '../../../../types/listeningTypes';
import { apiClient } from '../../../clientUtils/apiClient';
import { useMobxInstance } from '../../../clientUtils/mobxUtil';
import modalStore from '../../../stores/modalStore';
import { LessonPlayerRef } from '../LessonPlayer';
import firebaseStore from '../../../stores/firebaseStore';
import { useCommonNavigate } from '../../../views/common/CommonLink';

const LESSON_STATUS_POLLING_INTERVAL = 7000;

const LESSON_THINKING_TEXT_MESSAGES = [
    '레슨을 만들고 있어요...',
    '영상을 분석하고 있습니다...',
    '1분에서 5분까지 걸릴 수 있어요...',
    '자막을 분석하고 있습니다...',
    '최고의 레슨을 만들기 위해 노력 중...',
    '문장을 추출하고 있습니다...',
    '페이지를 나갔다 와도 괜찮아요...',
    '챕터를 분리하고 있습니다...',
    '열심히 레슨을 만들고 있어요.',
    '챕터 내용을 정리하고 있습니다...',
    '다른 작업을 하고 오셔도 괜찮아요...',
    '이제 거의 완료됐어요...',
    '조금만 더 기다려주세요...',
    '앗, 잠시 뒤에 새로고침 해보세요.',
];

/**
 * 레슨 정보 관리
 * - 레슨 메타데이터
 * - 챕터 목록
 * - 현재 챕터 번호 및 문장 번호 (URL과 동기화)
 */
export class LessonStore {
    readonly lessonHash: string;

    lesson: LessonResponse | null = null;
    userLesson: UserLessonResponse | null = null;
    chapterList: ChapterListItemResponse[] = [];

    isLoading = true;

    // URL과 동기화되는 현재 위치
    // - 페이지가 초기화될 때 쿼리 스트링으로 초기화됨 (LessonPage)
    currentChapterNum: number = 0;
    currentSentenceNum: number = 1;

    pollingTimeout: NodeJS.Timeout | null = null;
    pollingCount: number = 0;

    // 레슨 플레이어 참조
    lessonPlayerRef: RefObject<LessonPlayerRef | null> | null = null;

    // 시간 업데이트 콜백 (ChapterProvider에서 등록)
    private onTimeUpdateCallback: ((currentTime: number) => void) | null = null;

    constructor(lessonHash: string) {
        makeAutoObservable(this);
        this.lessonHash = lessonHash;
    }

    /**
     * 레슨 플레이어 참조 설정
     */
    setLessonPlayerRef(ref: RefObject<LessonPlayerRef | null> | null) {
        this.lessonPlayerRef = ref;
    }

    /**
     * 시간 업데이트 콜백 등록 (ChapterProvider에서 호출)
     */
    setOnTimeUpdateCallback(callback: ((currentTime: number) => void) | null) {
        this.onTimeUpdateCallback = callback;
    }

    /**
     * 시간 업데이트 처리 (LessonPlayer에서 호출)
     */
    handleTimeUpdate(currentTime: number) {
        this.onTimeUpdateCallback?.(currentTime);
    }

    get lessonStatus(): LessonStatus {
        return this.lesson?.status as LessonStatus;
    }

    get lessonStatusUpdatedAt(): string | null {
        return this.lesson?.statusUpdatedAt || null;
    }

    get hasLessonError(): boolean {
        return !!this.lesson && !!this.lesson.hasError;
    }

    /**
     * 레슨 생성 중 여부
     */
    get isCreatingLesson(): boolean {
        return (
            !this.hasLessonError &&
            !!this.lessonStatus &&
            this.lessonStatus !== 'none' &&
            this.lessonStatus !== 'completed'
        );
    }

    /**
     * 현재 챕터 객체
     */
    get currentChapter(): ChapterListItemResponse | null {
        return this.chapterList.find((ch) => ch.num === this.currentChapterNum) || null;
    }

    /**
     * 현재 챕터 해시 (API 호출용)
     */
    get currentChapterHash(): string | null {
        return this.currentChapter?.hash || null;
    }

    /**
     * 첫 번째 챕터 여부
     */
    get isFirstChapter(): boolean {
        return this.currentChapterNum === 1;
    }

    /**
     * 마지막 챕터 여부
     */
    get isLastChapter(): boolean {
        return this.chapterList.length > 0 && this.currentChapterNum === this.chapterList.length;
    }

    get lessonThinkingText(): string {
        // 대략 1분에서 3분 정도 걸림
        return LESSON_THINKING_TEXT_MESSAGES[this.pollingCount] || '';
    }

    get isLastLessonThinkingText(): boolean {
        return this.pollingCount >= LESSON_THINKING_TEXT_MESSAGES.length - 1;
    }

    /**
     * 학습 중인 레슨 데이터 로드
     */
    async fetchUserLesson() {
        this.isLoading = true;

        try {
            const userLesson = await apiClient.userLesson.member.getUserLesson.$query({
                lessonHash: this.lessonHash,
            });

            runInAction(() => {
                this.userLesson = userLesson;
            });
        } catch (e) {
            modalStore.error({
                error: e,
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /**
     * 레슨 데이터 로드
     */
    async fetchLesson() {
        this.isLoading = true;

        try {
            const lesson = await apiClient.lesson.public.getLesson.$query({
                lessonHash: this.lessonHash,
            });

            runInAction(async () => {
                this.lesson = lesson;

                // 레슨 생성이 완료되었고 오류가 없는 경우에만 챕터 목록을 불러옴
                if (this.lessonStatus === 'completed' && !this.hasLessonError) {
                    const chapterList = await apiClient.lesson.public.getChapterList.$query({
                        lessonHash: this.lessonHash,
                    });

                    runInAction(() => {
                        this.chapterList = chapterList || [];
                    });
                } else {
                    this.chapterList = [];
                }
            });
        } catch (e) {
            modalStore.error({
                error: e,
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    startLessonStatusPolling() {
        this.clearPollingTimeout();

        if (!this.isCreatingLesson) {
            return;
        }

        this.pollingTimeout = setTimeout(() => {
            this.fetchLessonStatusPolling();
        }, LESSON_STATUS_POLLING_INTERVAL);
    }

    /**
     * 5초마다 레슨 상태 폴링
     */
    async fetchLessonStatusPolling() {
        this.clearPollingTimeout();

        if (!this.isCreatingLesson) {
            return;
        }

        const lessonStatus = await apiClient.lesson.public.getLessonStatus.$forceQuery({
            lessonHash: this.lessonHash,
        });
        if (!lessonStatus) {
            return;
        }

        const { status, statusUpdatedAt, hasError } = lessonStatus;

        runInAction(async () => {
            // 오류가 발생했으면 polling 종료하고 재시도 버튼 표시
            if (hasError) {
                runInAction(() => {
                    this.lesson = {
                        ...this.lesson!,

                        status,
                        statusUpdatedAt,
                        hasError: true,
                    };
                    this.chapterList = [];
                });
                return;
            } else if (status === 'completed') {
                // 완료되었으면 캐시 초기화하고 다시 불러옴
                apiClient.lesson.public.getLesson.$invalidate({
                    lessonHash: this.lessonHash,
                });
                apiClient.lesson.public.getChapterList.$invalidate({
                    lessonHash: this.lessonHash,
                });

                this.fetchLesson();
            } else {
                // 아직 진행 중이면 계속 폴링
                this.pollingCount++;

                if (this.isLastLessonThinkingText) {
                    // 마지막 메시지인 경우 polling 을 종료한다
                    this.clearPollingTimeout();
                } else {
                    this.startLessonStatusPolling();
                }
            }
        });
    }

    clearPollingTimeout() {
        if (this.pollingTimeout) {
            clearTimeout(this.pollingTimeout);
        }
    }

    /**
     * 챕터 변경
     */
    setChapter(chapterNum: number, sentenceNum: number = 1) {
        // chapterNum 유효성 검사
        if (chapterNum < 1 || chapterNum > this.chapterList.length) {
            console.warn(
                `Invalid chapterNum: ${chapterNum}, valid range: 1~${this.chapterList.length}`,
            );
            return;
        }

        this.currentChapterNum = chapterNum;
        this.currentSentenceNum = sentenceNum;
    }

    /**
     * 문장 변경 (ChapterProvider에서 콜백으로 호출됨)
     */
    setSentence(sentenceNum: number) {
        this.currentSentenceNum = sentenceNum;
    }

    /**
     * 다음 챕터로 이동
     */
    nextChapter() {
        if (!this.isLastChapter) {
            this.setChapter(this.currentChapterNum + 1, 1);
        }
    }

    /**
     * 이전 챕터로 이동
     */
    previousChapter() {
        if (!this.isFirstChapter) {
            this.setChapter(this.currentChapterNum - 1, 1);
        }
    }

    /**
     * 레슨 생성 재시도
     */
    async retryCreateLesson() {
        this.pollingCount = 0;

        const lessonStatus = await apiClient.userLesson.member.retryCreateLesson.mutate({
            lessonHash: this.lessonHash,
        });
        if (!lessonStatus) {
            return;
        }

        runInAction(() => {
            this.lesson = {
                ...this.lesson!,

                status: lessonStatus.status,
                statusUpdatedAt: lessonStatus.statusUpdatedAt,
                hasError: lessonStatus.hasError,
            };
            this.chapterList = [];
        });

        if (this.isCreatingLesson) {
            this.startLessonStatusPolling();
        }
    }
}

const LessonContext = createContext<LessonStore | null>(null);

/**
 * 학습 중인 레슨을 감싸는 프로바이더
 */
export const LessonProvider = observer(
    ({ lessonHash, children }: { lessonHash: string; children: ReactNode }) => {
        const lessonStore = useMobxInstance(LessonStore, lessonHash);
        const { navigate } = useCommonNavigate();

        useEffect(() => {
            (async () => {
                const isLoggedIn = await firebaseStore.isLoggedIn();
                if (!isLoggedIn) {
                    // 로그인하지 않았으면 레슨 소개 페이지로 이동
                    navigate(`/lesson/${lessonStore.lessonHash}`);
                    return;
                }

                await lessonStore.fetchUserLesson();
                if (!lessonStore.userLesson) {
                    // 유저 레슨이 없으면 레슨 소개 페이지로 이동
                    navigate(`/lesson/${lessonStore.lessonHash}`);
                    return;
                }

                await lessonStore.fetchLesson();

                // 레슨 상태 폴링 시작
                lessonStore.startLessonStatusPolling();
            })();

            return () => {
                // 리소스 정리
                lessonStore.clearPollingTimeout();
                lessonStore.setLessonPlayerRef(null);
            };
        }, [lessonStore, navigate]);

        return <LessonContext.Provider value={lessonStore}>{children}</LessonContext.Provider>;
    },
);

/**
 * 레슨 스토어 사용 훅
 */
export const useLessonStore = (): LessonStore => {
    const lessonStore = useContext(LessonContext);

    if (!lessonStore) {
        throw new Error('useLessonStore must be used within a LessonProvider');
    }

    return lessonStore;
};
